import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import ProfilePic from "../../../assets/images/blank-profile-pic.png";
import { useAlert } from "../../constants/AlertProvider";
import Navbar from "../../constants/Navbar";
import RecHomeNavbar from "./RecHomeNavbar";
import ImageModal from "../common/ImageModal";
import Select from "../common/Select";
import { getUserDetails } from "../../utils/getUserDetails"

export default function RecProfile() {
    const companySizeOptions = [
        { value: "sole", label: "Just myself (Sole Proprietor)" },
        { value: "2_5", label: "2-5" },
        { value: "16_30", label: "16-30" },
        { value: "31_50", label: "31-50" },
        { value: "50_plus", label: "More than 50" },
    ];
    const industryOptions = [
        { value: "retail", label: "Retail" },
        { value: "food_beverage", label: "Food & Beverage" },
        { value: "professional_services", label: "Professional Services" },
        { value: "healthcare", label: "Healthcare / Wellness" },
        { value: "trades", label: "Trades / Services" },
        { value: "ecommerce", label: "E - commerce / Online Business" },
        { value: "creative", label: "Creative / Design" },
        { value: "nonprofit", label: "Non - profit" },
        { value: "other", label: "Other" },
    ];
    const titleOptions = [
        { value: "owner", label: "Owner / Founder" },
        { value: "ceo", label: "CEO / President" },
        { value: "gm", label: "General Manager" },
        { value: "marketing", label: "Marketing Manager" },
        { value: "operations", label: "Operations Manager" },
        { value: "hr", label: "HR Manager" },
        { value: "finance", label: "Finance Manager" },
        { value: "sales", label: "Sales Manager" },
        { value: "it", label: "IT Manager" },
        { value: "customer_service", label: "Customer Service Manager" },
        { value: "consultant", label: "Consultant" },
        { value: "recruiter", label: "Recruiter" },
        { value: "other", label: "Other" },
    ];

    const location = useLocation();
    const isEditMode = location.state?.from === "edit";
    // const [showUserImageModal, setShowUserImageModal] = useState(false);
    const [showCompanyLogoModal, setShowCompanyLogoModal] = useState(false);
    const [userData, setUserData] = useState({
        name: "",
        email: "",
        business_role: "",
    })
    const [companyData, setCompanyData] = useState({
        name: "",
        address: "",
        description: "",
        website: "",
        logo: null,
        // contact_email: "",
        // phone_number: "",
        // is_small_business: false,
        // is_nonprofit: false,
        industry: "",
        employee_count: "",
        culture: "",
        // hiring_goals: "",
        // roles_hiring_for: "",
        // positions_to_hire: null
    })
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoToUpload, setLogoToUpload] = useState(null);
    const navigate = useNavigate();
    const showAlert = useAlert();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userDetails = await getUserDetails();
                const userId = userDetails?.id;
                const userRes = await axios.get(`/api/users/${userId}/`, { withCredentials: true });
                setUserData(userRes.data);
                const companyId = userRes.data.company;
                if (companyId) {
                    const companyRes = await axios.get(`/api/company/${companyId}/`, { withCredentials: true });
                    setCompanyData(companyRes.data);
                }
            } catch (error) {
                showAlert("Something went wrong!", "error");
                console.error("error", error);
            }
        };
        fetchData();
    }, [])

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const userDetails = await getUserDetails();
            const userId = userDetails?.id;
            let companyId = userData.company;

            // Prepare company data for submission
            const companyFormData = new FormData();
            Object.keys(companyData).forEach(key => {
                // Don't append the logo URL from the server or null/undefined values
                if (key !== 'logo' && companyData[key] !== null && companyData[key] !== undefined && companyData[key] !== '') {
                    companyFormData.append(key, companyData[key]);
                }
            });
            if (logoToUpload) {
                companyFormData.append('logo', logoToUpload);
            }

            const userPatchData = { ...userData };

            // If user has no company, create one
            if (!companyId) {
                const companyRes = await axios.post("/api/company/", companyFormData, {
                    headers: {
                        'X-CSRFToken': Cookies.get('csrftoken'),
                    },
                    withCredentials: true,
                });
                companyId = companyRes.data.id;
                userPatchData.company = companyId;
            } else {
                // PATCH company profile
                await axios.patch(`/api/company/${companyId}/`, companyFormData, {
                    headers: {
                        'X-CSRFToken': Cookies.get('csrftoken'),
                    },
                    withCredentials: true,
                });
            }

            // Patch the user with the consolidated data (including the new company ID if applicable)
            await axios.patch(`/api/users/${userId}/`, userPatchData, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken'),
                },
                withCredentials: true,
            });

            showAlert("Profile saved!", "success");
            navigate("/recpreferences");
        } catch (error) {
            showAlert("Error saving profile", "error");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCompanyData((prev) => ({ ...prev, [name]: value }));
    };
    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCompanyLogoCropped = (file) => {
        setLogoToUpload(file);
        setShowCompanyLogoModal(false);
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Navbar */}
            
            {isEditMode ? <RecHomeNavbar /> : <Navbar />}

            {/* Main Container */}
            <form className={isEditMode ? "mt-14 flex flex-col px-4 sm:px-6 lg:px-8 py-10" : "flex flex-col px-4 sm:px-6 lg:px-8 py-10"} onSubmit={handleSave}>
                <main className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-900">
                    {/* Heading */}
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-10 text-center lg:text-left">
                        {isEditMode ? "Edit Your Profile" : "Let's begin by setting up your hiring profile!"}
                    </h1>
                    <div className="flex flex-col gap-y-12">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-6">
                            <div className="lg:col-span-1 flex flex-col gap-y-2">
                                <h3 className="text-base text-gray-900 dark:text-gray-100 font-semibold">Company Profile</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-normal">Tell us about your organization</p>
                            </div>
                            <div className="lg:col-span-2 flex flex-col">
                                <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="company-name" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Company Name</label><input id="company-name" name="name" value={companyData.name} onChange={handleChange} type="text" className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div>
                                <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="Company-website" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Company Website</label>
                                    <input id="Company-website" name="website" value={companyData.website} onChange={handleChange} type="url" placeholder="http://company.com/" className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div>

                                {/* comapny-size, industry/markets */}
                                <div className="w-full md:w-4/5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 mb-8">
                                    <div className="flex flex-col gap-y-2">
                                        <Select
                                            label="Company Size"
                                            name="employee_count"
                                            value={companyData.employee_count}
                                            options={companySizeOptions}
                                            onChange={handleChange}
                                            placeholder="Select company size"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-y-2">
                                        <Select
                                            label="Industry"
                                            name="industry"
                                            value={companyData.industry}
                                            options={industryOptions}
                                            onChange={handleChange}
                                            placeholder="Select industry"
                                        />
                                    </div>
                                </div>

                                {/* description */}
                                <div className="w-full md:w-4/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="description" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Description</label>
                                    <textarea id="description" name="description" value={companyData.description} onChange={handleChange} rows={2} cols={4} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500 resize-none" />
                                    <p className="text-gray-600 dark:text-gray-400 font-normal text-sm">Write about your company</p>
                                </div>
                                {/* address */}
                                <div className="w-full md:w-4/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="address" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Address</label>
                                    <input id="address" name="address" value={companyData.address} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500 resize-none" />
                                </div>
                                {/* culture */}
                                <div className="w-full md:w-4/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="culture" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Company Culture</label>
                                    <textarea id="culture" name="culture" value={companyData.culture} onChange={handleChange} rows={2} cols={4} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500 resize-none" />
                                </div>

                                {/* profile-photo */}
                                <div className="flex flex-col gap-y-2 mb-8">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Photo</p>
                                    <div className="flex flex-row gap-x-4 items-center mb-8">
                                        <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-md overflow-hidden">
                                            <img src={logoToUpload ? URL.createObjectURL(logoToUpload) : companyData.logo || ProfilePic} alt="Company Logo" className="w-full h-full object-cover" />
                                        </div>
                                        <button className="w-18 h-8 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-[14px] font-semibold"
                                            type="button"
                                            onClick={() => setShowCompanyLogoModal(true)}
                                        >
                                            Change
                                        </button>
                                    </div>
                                    {showCompanyLogoModal && (
                                        <ImageModal
                                            closeModal={() => setShowCompanyLogoModal(false)}
                                            onImageCropped={handleCompanyLogoCropped}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-6">
                            <div className="lg:col-span-1 flex flex-col gap-y-2">
                                <h3 className="text-base text-gray-900 dark:text-gray-100 font-semibold">Your Profile</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-normal">A little about you</p>
                            </div>
                            <div className="lg:col-span-2 flex flex-col">
                                <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="full-name" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Full Name</label>
                                    <input id="full-name" name="name" value={userData.name || ''} onChange={handleProfileChange} type="text" className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div>
                                {/* email */}
                                <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="email" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Email</label>
                                    <input id="email" name="email" value={userData.email} onChange={handleProfileChange} type="email" className="border border-gray-300 p-2 rounded-lg text-sm text-gray-900 font-medium focus:outline-none focus:border-blue-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed" disabled />
                                </div>
                                {/* role */}
                                <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <Select
                                        label="Title / Role"
                                        name="business_role"
                                        value={userData.business_role}
                                        options={titleOptions}
                                        onChange={handleProfileChange}
                                        placeholder="Select Title / Role"
                                    />
                                </div>
                                {/* <div className="w-full md:w-3/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="profile" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Profile</label>
                                    <input id="profile" name="profileUrl" value={userData.profileUrl} onChange={handleProfileChange} type="url" placeholder="https://linkedin.com/in/..." className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div> */}
                                {/* profile-photo
                                <div className="flex flex-col gap-y-2 mb-8">
                                    <p className="text-sm font-medium text-gray-900">Your Photo</p>
                                    <div className="flex flex-row gap-x-4 items-center mb-8">
                                        <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-md overflow-hidden">
                                            <img src={userDataPic || ProfilePic} alt="User Profile" className="w-full h-full object-cover" />
                                        </div>
                                        <button className="w-18 h-8 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-[14px] font-semibold"
                                            type="button"
                                            onClick={() => setShowUserImageModal(true)}
                                        >
                                            Change
                                        </button>
                                    </div>
                                    {showUserImageModal && (
                                        <ImageModal
                                            closeModal={() => setShowUserImageModal(false)}
                                            onImageCropped={handleUserImageCropped}
                                        />
                                    )}
                                </div> */}
                            </div>
                        </div>
                    </div>
                </main>
                <div className="w-full flex justify-end p-6">
                    <button type="submit" disabled={isSubmitting} className="text-white h-10 text-center font-medium text-sm bg-blue-700 px-4 rounded-lg cursor-pointer hover:bg-blue-600 disabled:bg-blue-400">
                        {isSubmitting ? "Saving..." : isEditMode ? "Next, Edit Your Preferences" : "Next, Add Preferences"}
                    </button>
                </div>
            </form>
        </div>
    );
}